import React from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MoreVertical16Regular } from '@fluentui/react-icons';
import FileReadsIndicator from '../components/FileReadsIndicator';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { format } from 'date-fns';
import { activeIndexingFileIds, failedIndexingFileIds } from '../../../utils/vdrIndexingQueue';
import styles from '../css/vdr.module.css';
import { StatusDot, StatusFilters, SearchField, normalizeStatus } from '../components/StatusUI';
import { VDRFolderIcon, VDRDocumentIcon } from '../components/VDRItemIcons';

const formatDate = value => {
    if (!value) return '—';
    try {
        return format(new Date(value), 'd MMM yyyy');
    } catch {
        return '—';
    }
};

const fileExtension = name => {
    const parts = String(name || '').split('?')[0].split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const normalizeExampleDocuments = value => {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list
        .map(item => {
            if (typeof item === 'string') return item.trim();
            if (item && typeof item === 'object') {
                return String(item.name || item.title || item.document || '').trim();
            }
            return '';
        })
        .filter(Boolean);
};

const FolderView = ({
    workstream,
    folder,
    subfolders = [],
    files = [],
    selectedRows = [],
    setSelectedRows,
    selectedFileId,
    onOpenFile,
    onOpenFileInfo,
    onOpenSubfolder,
    onEditFolder,
    onUploadFiles,
    projectUsersAll,
    indexingFiles = [],
    selectedProject,
    // Optional controlled status-filter helpers from parent
    statusFilter: statusFilterProp,
    setStatusFilter: setStatusFilterProp,
    folderQuery: folderQueryProp,
    setFolderQuery: setFolderQueryProp,
    fileCountsByFolder = {},
    filesLoading = false,
}) => {
    const [localQuery, setLocalQuery] = React.useState('');
    const [localStatusFilter, setLocalStatusFilter] = React.useState('all');
    const [folderTrail, setFolderTrail] = React.useState([]);
    const navigatingRef = React.useRef(false);

    const query = folderQueryProp ?? localQuery;
    const setQuery = setFolderQueryProp || setLocalQuery;
    const statusFilter = statusFilterProp ?? localStatusFilter;
    const setStatusFilter = setStatusFilterProp || setLocalStatusFilter;

    React.useEffect(() => {
        if (navigatingRef.current) {
            navigatingRef.current = false;
            return;
        }
        setFolderTrail([]);
    }, [folder?.id, workstream?.id]);

    const indexingIds = React.useMemo(
        () => new Set(activeIndexingFileIds(indexingFiles, { projectId: selectedProject?.id })),
        [indexingFiles, selectedProject?.id],
    );
    const failedIds = React.useMemo(
        () => new Set(failedIndexingFileIds(indexingFiles, { projectId: selectedProject?.id })),
        [indexingFiles, selectedProject?.id],
    );

    const getUserName = id => {
        if (id == null) return '—';
        const u = projectUsersAll?.find(item => item?.value?.group_member_id === id);
        return u?.value?.full_name || '—';
    };

    const resolveStatus = file => {
        const id = Number(file?.id);
        if (failedIds.has(id) || failedIds.has(file?.id)) return 'needs_attention';
        if (indexingIds.has(id) || indexingIds.has(file?.id)) return 'processing';
        return normalizeStatus(file?.indexing_status || file?.status || file?.index_status);
    };

    const filteredFiles = (files || []).filter(f => {
        const name = String(f.document_name || f.name || '');
        if (query && !name.toLowerCase().includes(String(query).toLowerCase())) return false;
        if (statusFilter !== 'all' && resolveStatus(f) !== statusFilter) return false;
        return true;
    });

    const toggleRow = (id, e) => {
        e?.stopPropagation?.();
        if (!setSelectedRows) return;
        if (selectedRows.includes(id)) setSelectedRows(selectedRows.filter(x => x !== id));
        else setSelectedRows([...selectedRows, id]);
    };

    const toggleAll = checked => {
        if (!setSelectedRows) return;
        if (checked) setSelectedRows(filteredFiles.map(f => f.id));
        else setSelectedRows([]);
    };

    const allSelected = filteredFiles.length > 0 && filteredFiles.every(f => selectedRows.includes(f.id));

    const handleOpenSubfolder = sf => {
        if (folder) {
            navigatingRef.current = true;
            setFolderTrail(prev => [...prev, folder]);
        }
        onOpenSubfolder?.(sf);
    };

    const handleTrailClick = index => {
        const target = folderTrail[index];
        if (!target) return;
        navigatingRef.current = true;
        setFolderTrail(prev => prev.slice(0, index));
        onOpenSubfolder?.(target);
    };

    const exampleDocuments = React.useMemo(
        () => normalizeExampleDocuments(folder?.example_documents),
        [folder?.example_documents],
    );

    return (
        <>
            <div className={styles.folderBreadcrumbRow}>
                <div className={styles.breadcrumb}>
                    <span>{workstream?.name}</span>
                    {folderTrail.map((crumb, idx) => (
                        <React.Fragment key={crumb.id || idx}>
                            <span className={styles.breadcrumbSep}>/</span>
                            <button type="button" className={styles.breadcrumbLink} onClick={() => handleTrailClick(idx)}>
                                {crumb.name}
                            </button>
                        </React.Fragment>
                    ))}
                    <span className={styles.breadcrumbSep}>/</span>
                    <span className={styles.breadcrumbActive}>{folder?.name}</span>
                </div>
                <div className={styles.folderHeaderActions}>
                    <button type="button" className={styles.textLink} onClick={onEditFolder}>
                        Edit folder
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={onUploadFiles}>
                        Upload files
                    </button>
                </div>
            </div>

            <div className={styles.contentHeader}>
                <div>
                    <h2 className={styles.contentTitle}>{folder?.name}</h2>
                    {folder?.description ? <p className={styles.folderContentDesc}>{folder.description}</p> : null}
                    {exampleDocuments.length > 0 ? (
                        <div className={styles.folderExamples}>
                            <p className={styles.folderExamplesTitle}>Example documents</p>
                            <ul className={styles.folderExamplesList}>
                                {exampleDocuments.map(example => (
                                    <li key={example}>{example}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            </div>

            <div id="vdr-selection-anchor" />

            <div className={styles.toolbar}>
                <SearchField value={query} onChange={setQuery} placeholder="Search in folder" variant="inline" />
                <StatusFilters value={statusFilter} onChange={setStatusFilter} variant="inline" />
            </div>

            <div className={styles.table}>
                <div className={`${styles.tableHead} ${styles.filesCols}`}>
                    <span>
                        <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={allSelected}
                            onChange={e => toggleAll(e.target.checked)}
                            aria-label="Select all files"
                        />
                    </span>
                    <span>Name</span>
                    <span>Date</span>
                    <span>Uploaded</span>
                    <span>Uploaded by</span>
                    <span>Status</span>
                    <span>Reads</span>
                    <span />
                </div>

                {(subfolders || []).map(sf => {
                    const count = fileCountsByFolder[sf.id];
                    return (
                        <div
                            key={`folder-${sf.id}`}
                            className={`${styles.tableRow} ${styles.filesCols}`}
                            onClick={() => handleOpenSubfolder(sf)}
                        >
                            <span />
                            <div className={styles.tableRowName}>
                                <VDRFolderIcon />
                                <span className={styles.folderRowName}>{sf.name}</span>
                                {count != null && (
                                    <span className={styles.folderItemCount}>
                                        {count} item{count === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>
                            <span className={styles.subMeta}>{formatDate(sf.updated_at || sf.created)}</span>
                            <span />
                            <span />
                            <span />
                            <span />
                            <span onClick={e => e.stopPropagation()}>
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <button type="button" className={styles.iconBtn} aria-label="More">
                                            <MoreVertical16Regular color="#c6c8d2" />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem onClick={() => handleOpenSubfolder(sf)}>Open</MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </span>
                        </div>
                    );
                })}

                {filesLoading && (subfolders || []).length > 0 ? (
                    <div className={styles.tableFilesLoading} role="status" aria-live="polite">
                        <AiOutlineLoading3Quarters className="rotateLoader" aria-hidden />
                        <span>Loading files…</span>
                    </div>
                ) : null}

                {filteredFiles.map(file => {
                    const name = file.document_name || file.name || 'Untitled';
                    const selected = selectedRows.includes(file.id);

                    return (
                        <div
                            key={file.id}
                            className={`${styles.tableRow} ${styles.filesCols} ${selected || selectedFileId === file.id ? styles.tableRowSelected : ''}`}
                            onClick={() => onOpenFileInfo?.(file)}
                        >
                            <span onClick={e => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={selected}
                                    onChange={e => toggleRow(file.id, e)}
                                    aria-label={`Select ${name}`}
                                />
                            </span>
                            <div className={styles.tableRowName}>
                                <VDRDocumentIcon file={file} extension={fileExtension(name)} />
                                <span
                                    className={styles.fileRowName}
                                    role="button"
                                    tabIndex={0}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onOpenFile?.(file);
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation();
                                            onOpenFile?.(file);
                                        }
                                    }}
                                >
                                    {name}
                                </span>
                            </div>
                            <span className={styles.subMeta}>
                                {formatDate(file.metadata_creation_date || file.document_date)}
                            </span>
                            <span className={styles.subMeta}>
                                {formatDate(file.created || file.upload_date || file.uploaded_at)}
                            </span>
                            <span className={styles.subMeta}>{getUserName(file.upload_by)}</span>
                            <StatusDot status={resolveStatus(file)} />
                            <span className={styles.subMeta}>
                                <FileReadsIndicator file={file} projectUsersAll={projectUsersAll} />
                            </span>
                            <span onClick={e => e.stopPropagation()}>
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <button type="button" className={styles.iconBtn} aria-label="More">
                                            <MoreVertical16Regular color="#989bad" />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem onClick={() => onOpenFile?.(file)}>Open</MenuItem>
                                            <MenuItem onClick={() => onOpenFileInfo?.(file)}>File details</MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </span>
                        </div>
                    );
                })}

                {filesLoading && (subfolders || []).length === 0 ? (
                    <div className={styles.tableFilesLoading} role="status" aria-live="polite">
                        <AiOutlineLoading3Quarters className="rotateLoader" aria-hidden />
                        <span>Loading files…</span>
                    </div>
                ) : null}

                {!filesLoading && filteredFiles.length === 0 && (subfolders || []).length === 0 && (
                    <div className={styles.emptyState}>No files in this folder</div>
                )}
            </div>
        </>
    );
};

export default FolderView;
